import { type FC } from "react";
import {
  Checkbox,
  Typography,
  Card,
  Grid,
  Box,
  Stack,
  IconButton,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import type { PostsItem } from "@/partner/entities/posts";
import { ItemTitleLink } from "@/partner/shared/components/ItemTitleLink";
import { PagePaths } from "@/partner/shared/constants";

const StyledCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== "selected",
})<{ selected?: boolean }>(({ theme, selected }) => ({
  backgroundColor: selected
    ? theme.palette.action.selected
    : theme.palette.background.paper,
  padding: theme.spacing(2, 1),
  overflow: "visible",
  position: "relative",
}));

interface PostsListItemProps {
  post: PostsItem;
  selected: boolean;
  onSelectOneChange: (
    id: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onClickItem: (id: string) => void;
}

export const PostsListItem: FC<PostsListItemProps> = ({
  post,
  selected,
  onSelectOneChange,
  onClickItem,
}) => {
  const { t } = useTranslation();

  const style = {
    opacity: post.publishAt ? 1 : 0.6,
  };

  const tagsLabel = post.tags?.map((tag) => tag.title).join(", ") ?? "";

  return (
    <StyledCard selected={selected} style={style} elevation={0}>
      <Grid container spacing={2} wrap="nowrap" alignItems="flex-start">
        <Grid size="auto">
          <Checkbox
            color="default"
            checked={selected}
            onChange={(event) => onSelectOneChange(post.id, event)}
            onClick={(event) => event.stopPropagation()}
          />
        </Grid>

        <Grid size="grow">
          <Box
            sx={{
              ml: { xs: 1.5, md: 0 },
              mr: { xs: 1.5, md: 0 },
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box sx={{ flex: 1 }}>
                <ItemTitleLink
                  to={`${PagePaths.posts}?tab=edit&id=${post.id}`}
                  onOpen={() => onClickItem(post.id)}
                  underline="hover"
                  variant="h6"
                  color="textPrimary"
                  sx={{
                    fontSize: { xs: "0.9rem", sm: "1.1rem", md: "1.2rem" },
                  }}
                >
                  {post.title}
                </ItemTitleLink>

                {tagsLabel && (
                  <Box sx={{ mt: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {tagsLabel}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Stack>
          </Box>
        </Grid>
        <Grid size="auto" container justifyContent="flex-end">
          {post.publishAt ? (
            <Box>
              <Typography variant="body2" color="textPrimary" align="right">
                {t("posts.item.publishedAtTime", {
                  publishAt: post.publishAt,
                })}
              </Typography>
              <Typography variant="body2" color="textSecondary" align="right">
                {t("posts.item.publishedAtDate", {
                  publishAt: post.publishAt,
                })}
              </Typography>
            </Box>
          ) : (
            <Typography
              variant="body2"
              color="textPrimary"
              align="right"
              sx={{ pr: 2 }}
            >
              {t("posts.item.notPublished")}
            </Typography>
          )}
        </Grid>

        <Grid size="auto">
          <Stack direction="row" alignItems="center" spacing={1}>
            {post.url && (
              <IconButton
                target="_blank"
                href={post.url}
                size="large"
                component="a"
              >
                <OpenInNewIcon />
              </IconButton>
            )}
          </Stack>
        </Grid>
      </Grid>
    </StyledCard>
  );
};
